import { STATUS_COLORS } from './constants';

/**
 * Formats a duration in milliseconds to a human-readable string.
 * Rules:
 * - < 1000ms: "Nms"
 * - < 60s: "Ns" with 1 decimal (e.g. 6000ms -> "6.0s")
 * - >= 60s: "Xm Ys" (e.g. 65000ms -> "1m 5s")
 *
 * @param {number|null|undefined} ms
 * @returns {string}
 */
export function formatDuration(ms) {
    // Preserve previous Studio UX: missing/zero duration shows "0s"
    if (!ms) return '0s';
    const n = typeof ms === 'number' ? ms : Number(ms);
    if (!Number.isFinite(n) || n < 0) return '0s';

    if (n < 1000) {
        return `${Math.round(n)}ms`;
    }

    if (n < 60000) {
        return `${(n / 1000).toFixed(1)}s`;
    }

    const minutes = Math.floor(n / 60000);
    const seconds = Math.floor((n % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
}

/**
 * Returns a hex colour string for a given step status.
 *
 * @param {string} status  e.g. 'success' | 'failed' | 'running' | 'pending' | 'skipped'
 * @returns {string}
 */
export function getStatusColor(status) {
    return STATUS_COLORS[status] ?? STATUS_COLORS.default;
}

/**
 * Calculates the total number of AI steps across all instructions.
 *
 * @param {Array} instructions
 * @returns {number}
 */
export function getTotalSteps(instructions = []) {
    return instructions.reduce((sum, inst) => sum + (inst.aiSteps?.length || 0), 0);
}

/**
 * Calculates the flat (0-based) index for a step given its instruction and step indices.
 * Matches the ordering used by buildPlanFromInstructions on the backend.
 *
 * @param {Array}  instructions
 * @param {number} instIdx      index of the parent instruction
 * @param {number} stepIdx      index of the step within that instruction
 * @returns {number}
 */
export function getFlatStepIndex(instructions, instIdx, stepIdx) {
    let idx = 0;
    for (let i = 0; i < instIdx; i++) {
        idx += (instructions[i].aiSteps?.length || 0);
    }
    return idx + stepIdx;
}

/**
 * Re-keys replayProgressMap after a step insert or delete.
 *
 * mode='insert': shifts all entries at index >= flatIndex up by count (default 1).
 * mode='delete': drops the entry at flatIndex, shifts everything above it down by 1.
 *
 * @param {Object} map        replayProgressMap keyed by flat step index
 * @param {number} flatIndex  insertion or deletion position
 * @param {'insert'|'delete'} mode
 * @param {number} [count=1]  number of steps inserted (insert mode only)
 * @returns {Object}
 */
export function reindexProgressMap(map, flatIndex, mode, count = 1) {
    if (!map || Object.keys(map).length === 0) return map;
    const reindexed = {};
    Object.entries(map).forEach(([key, value]) => {
        const k = Number(key);
        if (mode === 'delete') {
            if (k < flatIndex) reindexed[k] = value;
            else if (k > flatIndex) reindexed[k - 1] = value;
            // k === flatIndex: entry dropped intentionally
        } else {
            reindexed[k < flatIndex ? k : k + count] = value;
        }
    });
    return reindexed;
}

/**
 * Re-keys replayExecutionResults (results[] + stepDetails{}) after a step insert or delete.
 *
 * mode='insert': shifts all entries at index >= flatIndex up by count (default 1).
 * mode='delete': drops the entry at flatIndex, shifts everything above it down by 1.
 *
 * @param {Object|null} replayResults  { results: [], stepDetails: {}, ...rest }
 * @param {number} flatIndex           insertion or deletion position
 * @param {'insert'|'delete'} mode
 * @param {number} [count=1]           number of steps inserted (insert mode only)
 * @returns {Object|null}
 */
export function reindexReplayResults(replayResults, flatIndex, mode, count = 1) {
    if (!replayResults || flatIndex < 0) return replayResults;
    let changed = false;

    const reindexedResults = Array.isArray(replayResults.results)
        ? replayResults.results.reduce((acc, result) => {
            const idx = typeof result?.stepIndex === 'number' ? result.stepIndex : Number(result?.stepIndex);
            if (!Number.isFinite(idx)) { acc.push(result); return acc; }
            if (mode === 'delete') {
                if (idx === flatIndex) { changed = true; return acc; }
                if (idx > flatIndex) { changed = true; acc.push({ ...result, stepIndex: idx - 1 }); return acc; }
            } else {
                if (idx >= flatIndex) { changed = true; acc.push({ ...result, stepIndex: idx + count }); return acc; }
            }
            acc.push(result);
            return acc;
        }, [])
        : replayResults.results;

    const reindexedStepDetails = replayResults.stepDetails && typeof replayResults.stepDetails === 'object'
        ? Object.entries(replayResults.stepDetails).reduce((acc, [key, detail]) => {
            const idx = Number(key);
            if (!Number.isFinite(idx)) { acc[key] = detail; return acc; }
            if (mode === 'delete') {
                if (idx === flatIndex) { changed = true; return acc; }
                if (idx > flatIndex) { changed = true; acc[idx - 1] = { ...detail, stepIndex: idx - 1 }; return acc; }
            } else {
                if (idx >= flatIndex) { changed = true; acc[idx + count] = { ...detail, stepIndex: idx + count }; return acc; }
            }
            acc[idx] = detail;
            return acc;
        }, {})
        : replayResults.stepDetails;

    if (!changed) return replayResults;
    return { ...replayResults, results: reindexedResults, stepDetails: reindexedStepDetails };
}
